"use client"

import { useState } from "react"
import { GraduationCap, Users, Code, Megaphone, Coffee, MapPin, ChevronLeft, ChevronRight } from "lucide-react"
import "./CommunityPage.css"

const CommunityPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0)

  const initiatives = [
    {
      title: "Educational Workshops",
      description:
        "Comprehensive quantum education programs designed for learners at all levels, from beginners to advanced practitioners",
      icon: GraduationCap,
      gradient: "blue-cyan",
    },
    {
      title: "Research Collaborations",
      description:
        "Strategic partnerships with universities and institutions to advance quantum research and development",
      icon: Users,
      gradient: "purple-pink",
    },
    {
      title: "Coding & Algorithms",
      description:
        "Hands-on programming sessions focusing on quantum algorithms and practical quantum computing applications",
      icon: Code,
      gradient: "green-teal",
    },
    {
      title: "Community Outreach",
      description: "Public awareness campaigns and community engagement initiatives to democratize quantum knowledge",
      icon: Megaphone,
      gradient: "orange-red",
    },
    {
      title: "Networking Events",
      description:
        "Global meetups and mixers connecting quantum enthusiasts, researchers, and industry professionals worldwide",
      icon: Coffee,
      gradient: "indigo-purple",
    },
    {
      title: "Summit & Conferences",
      description:
        "Premier quantum events bringing together leading experts to share insights and shape the future of quantum technology",
      icon: MapPin,
      gradient: "cyan-blue",
    },
  ]

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % initiatives.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + initiatives.length) % initiatives.length)
  }

  return (
    <div className="community-page">
      {/* Hero Section */}
      <section className="community-hero">
        <div className="community-hero__container">
          <h1 className="community-hero__title">Join the Community</h1>
          <p className="community-hero__description">
            As part of the global quantum community, we're proud to contribute to the quantum ecosystem through various
            educational, research, and outreach initiatives across multiple categories.
          </p>
        </div>
      </section>

      {/* Community Initiatives */}
      <section className="iyq2025-initiatives-section">
        <div className="iyq2025-container">
          {/* Desktop Grid */}
          <div className="iyq2025-initiatives-grid desktop-grid">
            {initiatives.map((initiative, index) => (
              <div key={index} className={`iyq2025-initiative-card iyq2025-initiative-${initiative.gradient}`}>
                <div className="iyq2025-initiative-header">
                  <div className={`iyq2025-initiative-icon iyq2025-gradient-${initiative.gradient}`}>
                    <initiative.icon />
                  </div>
                  <h3 className="iyq2025-initiative-title">{initiative.title}</h3>
                </div>
                <div className="iyq2025-initiative-content">
                  <p className="iyq2025-initiative-description">{initiative.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Slider */}
          <div className="mobile-slider">
            <div className="slider-container">
              <button className="slider-arrow slider-arrow-left" onClick={prevSlide}>
                <ChevronLeft size={24} />
              </button>

              <div className="slider-wrapper">
                <div className="slider-track" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
                  {initiatives.map((initiative, index) => (
                    <div
                      key={index}
                      className={`iyq2025-initiative-card slider-card iyq2025-initiative-${initiative.gradient}`}
                    >
                      <div className="iyq2025-initiative-header">
                        <div className={`iyq2025-initiative-icon iyq2025-gradient-${initiative.gradient}`}>
                          <initiative.icon />
                        </div>
                        <h3 className="iyq2025-initiative-title">{initiative.title}</h3>
                      </div>
                      <div className="iyq2025-initiative-content">
                        <p className="iyq2025-initiative-description">{initiative.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button className="slider-arrow slider-arrow-right" onClick={nextSlide}>
                <ChevronRight size={24} />
              </button>
            </div>

            {/* Slider Indicators */}
            <div className="slider-indicators">
              {initiatives.map((_, index) => (
                <button
                  key={index}
                  className={`slider-indicator ${index === currentSlide ? "active" : ""}`}
                  onClick={() => setCurrentSlide(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Social Links Section */}
      <section className="community-social">
        <div className="community-social__container">
          <div className="community-social__links">
            <a
              href="https://github.com/quantumke-org"
              target="_blank"
              rel="noopener noreferrer"
              className="community-social__link community-social__link--github"
            >
              <svg className="community-social__icon" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
              <span>GitHub</span>
            </a>
            <a
              href="https://chat.whatsapp.com/JKgkJRJNYV9L9mqJkSxgcb"
              target="_blank"
              rel="noopener noreferrer"
              className="community-social__link community-social__link--whatsapp"
            >
              <svg className="community-social__icon" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488z" />
              </svg>
              <span>WhatsApp</span>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="community-cta">
        <div className="community-cta__container">
          <h2 className="community-cta__title">Join us now as we are building the future of quantum computing!</h2>
          <p className="community-cta__description">
            Be part of a growing community that's pushing the boundaries of what's possible with quantum computing
            technology.
          </p>
          <a
  href="https://forms.gle/Apt8Z7CFZJVWQS5a9"
  target="_blank"
  rel="noopener noreferrer"
>
  <button className="community-cta__button">
    JOIN NOW
    <svg className="community-cta__button-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  </button>
</a>

        </div>
      </section>
    </div>
  )
}

export default CommunityPage
